import React, { useEffect, useState } from "react";
import axios from "axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Row,
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  Form,
  FormGroup,
  FormText,
  Label,
  Col,
} from "reactstrap";
import { toast } from "react-toastify";
import { Colxx } from "components/common/CustomBootstrap";

import AppLayout from "layout/AppLayout";
import SelectInput from "components/common/SelectInput";
import ConfirmModal from "components/ConfirmModal";
import { useUser } from "auth/useUser";

const SELLER_TYPES = [
  { value: 0, label: "0 — resale" },
  { value: 1, label: "1 — manufacturer" },
  { value: 2, label: "2 — service" },
];

const blankPos = {
  name: "",
  psid: "",
  packageCode: "",
  unitCode: "",
  units: "шт",
  vat: 0,
  sellerType: 2,
  isDecimalUnits: false,
  barcode: "",
};

// Stored nulls become empty inputs; anything missing keeps its default.
const posFromRow = (pos) => ({
  ...blankPos,
  ...Object.fromEntries(
    Object.entries(pos || {}).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  ),
});

const AddPrice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "add";

  const user = useUser();
  const readOnly = user.role === "User" && !user.isSuperadmin;
  const canDelete = !isNew && (user.role === "Admin" || user.isSuperadmin);

  const [data, setData] = useState({
    exam: "",
    module: "",
    priceAmount: "",
    availableOnPos: false,
    pos: blankPos,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isNew) return;
    axios
      .get(`/api/details/info/${id}`)
      .then(({ data: row }) => {
        setData({
          exam: row.exam || "",
          module: row.module || "",
          priceAmount: row.priceAmount ?? "",
          availableOnPos: Boolean(row.availableOnPos),
          pos: posFromRow(row.pos),
        });
      })
      .catch(() => toast.error("Could not load this price"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const change = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    setErrorMessage("");
  };

  const changePos = (patch) => {
    setData({ ...data, pos: { ...data.pos, ...patch } });
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage("");
    try {
      // The server validates and casts everything; the terminal's product id
      // is assigned there and never sent from here.
      const { productId, updatedBy, updatedAt, ...pos } = data.pos;
      await axios.post(`/api/details/price/${isNew ? "add" : id}`, {
        exam: data.exam,
        module: data.module,
        priceAmount: data.priceAmount,
        availableOnPos: data.availableOnPos,
        pos,
      });
      toast.success(isNew ? "Price added" : "Price updated");
      navigate("/app/prices");
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || "Could not save this price",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/details/price/${id}`);
      toast.success("Price deleted");
      navigate("/app/prices");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not delete this price");
      setDeleting(false);
    }
  };

  const onPos = data.availableOnPos;
  const required = onPos ? " *" : "";

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <div className="loading" hidden={!loading} />
        {!loading && (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Colxx xxs="12" className="mb-4">
                <Card>
                  <CardBody>
                    <CardTitle>Price</CardTitle>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>Exam *</Label>
                          <Input
                            name="exam"
                            value={data.exam}
                            onChange={change}
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>Module *</Label>
                          <Input
                            name="module"
                            value={data.module}
                            onChange={change}
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>Price (UZS) *</Label>
                          <Input
                            type="number"
                            name="priceAmount"
                            value={data.priceAmount}
                            onChange={change}
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Colxx>

              <Colxx xxs="12" className="mb-4">
                <Card>
                  <CardBody>
                    <div className="d-flex flex-wrap justify-content-between align-items-start">
                      <CardTitle className="mb-2">POS terminal</CardTitle>
                      <FormGroup check className="mb-2">
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={onPos}
                            disabled={readOnly}
                            onChange={(e) => {
                              setData({
                                ...data,
                                availableOnPos: e.target.checked,
                              });
                              setErrorMessage("");
                            }}
                          />{" "}
                          <strong>Available on POS</strong>
                        </Label>
                      </FormGroup>
                    </div>

                    <Alert color="info" className="text-small">
                      Every receipt line needs a PSID (ИКПУ) and a package code
                      from the tax authority — look them up at{" "}
                      <a
                        href="https://tasnif.soliq.uz"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        tasnif.soliq.uz
                      </a>
                      . The receipt name must be unique and may only use
                      characters the OFD accepts; the Uzbek Latin apostrophe (ʻ)
                      is not one of them.
                    </Alert>

                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Receipt name{required}</Label>
                          <Input
                            value={data.pos.name}
                            onChange={(e) =>
                              changePos({ name: e.target.value })
                            }
                            disabled={readOnly}
                          />
                          <FormText>Shown on the fiscal receipt.</FormText>
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>PSID (ИКПУ){required}</Label>
                          <Input
                            value={data.pos.psid}
                            onChange={(e) =>
                              changePos({ psid: e.target.value })
                            }
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Package code{required}</Label>
                          <Input
                            type="number"
                            value={data.pos.packageCode}
                            onChange={(e) =>
                              changePos({ packageCode: e.target.value })
                            }
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2">
                        <FormGroup>
                          <Label>Units{required}</Label>
                          <Input
                            value={data.pos.units}
                            onChange={(e) =>
                              changePos({ units: e.target.value })
                            }
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label>Unit code</Label>
                          <Input
                            type="number"
                            value={data.pos.unitCode}
                            onChange={(e) =>
                              changePos({ unitCode: e.target.value })
                            }
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label>VAT{required}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={data.pos.vat}
                            onChange={(e) => changePos({ vat: e.target.value })}
                            disabled={readOnly}
                          />
                          <FormText>A fraction: 15% is 0.15</FormText>
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Seller type</Label>
                          <SelectInput
                            value={Number(data.pos.sellerType)}
                            onChange={(sellerType) => changePos({ sellerType })}
                            options={SELLER_TYPES}
                            isDisabled={readOnly}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Barcode</Label>
                          <Input
                            value={data.pos.barcode}
                            onChange={(e) =>
                              changePos({ barcode: e.target.value })
                            }
                            disabled={readOnly}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="6">
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={Boolean(data.pos.isDecimalUnits)}
                              onChange={(e) =>
                                changePos({ isDecimalUnits: e.target.checked })
                              }
                              disabled={readOnly}
                            />{" "}
                            Decimal units
                          </Label>
                        </FormGroup>
                      </Col>
                      {data.pos.productId && (
                        <Col md="6" className="text-md-right">
                          <span className="text-muted text-small">
                            Terminal product ID:{" "}
                            <code>{data.pos.productId}</code>
                          </span>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </Colxx>
            </Row>

            {errorMessage && (
              <Alert color="danger" className="mb-3">
                {errorMessage}
              </Alert>
            )}
            <div className="mb-4">
              <Button
                color="light"
                className="top-right-button"
                onClick={() => navigate("/app/prices")}
              >
                Cancel
              </Button>
              {!readOnly && (
                <Button
                  color="primary"
                  className="top-right-button ml-2"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? "Saving…" : isNew ? "Add" : "Update"}
                </Button>
              )}
              {canDelete && (
                <Button
                  type="button"
                  color="danger"
                  className="top-right-button ml-2"
                  onClick={() => setDeleteModal(true)}
                  disabled={deleting || saving}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </Button>
              )}
            </div>
          </Form>
        )}
        {deleteModal && (
          <ConfirmModal
            modal={deleteModal}
            setModal={setDeleteModal}
            title="Delete Price"
            body={`Delete the price for ${data.exam} — ${data.module}? It will no longer be available on the POS terminal.`}
            action={handleDelete}
            confirmLabel="Delete price"
            confirmColor="danger"
          />
        )}
      </div>
    </AppLayout>
  );
};

export default AddPrice;
